import React, { useState, useEffect, useRef, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getASNList,
  getSKUsByASN,
  getASNDetails,
  getLocations,
  submitPutAway
} from "../api/putaway";

import axios from "../api/client";

export default function PutAwayPage() {
  const [manualASN, setManualASN] = useState(false);
  const [manualLoc, setManualLoc] = useState(false);

  const [asnList, setAsnList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [skuList, setSkuList] = useState([]);

  const [asn, setAsn] = useState("");
  const [selectedASN, setSelectedASN] = useState("");

  const [sku, setSKU] = useState("");
  const [locationId, setLocationId] = useState("");
  const [enteredPieces, setEnteredPieces] = useState("");

  const [shipperID, setShipperID] = useState("");
  const [shipperName, setShipperName] = useState("");

  const [receivedTotal, setReceivedTotal] = useState(0);
  const [alreadyBinned, setAlreadyBinned] = useState(0);
  const [remaining, setRemaining] = useState(0);

  const userId = localStorage.getItem("UserID");

  const asnRef = useRef();
  const skuRef = useRef();
  const locRef = useRef();
  const pcsRef = useRef();

  const notify = (msg, type = "success") =>
    type === "success" ? toast.success(msg) : toast.error(msg);

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    const asnRes = await getASNList();
    const locRes = await getLocations();

    setAsnList(asnRes.data);
    setLocationList(locRes.data);

    setTimeout(() => asnRef.current?.focus(), 200);
  };

  // ---------------- ASN ----------------
  const handleASNInput = useCallback(async (value) => {
    const asnValue = manualASN ? value || selectedASN : value || asn;
    if (!asnValue) return;

    const skuRes = await getSKUsByASN(asnValue);
    setSkuList(skuRes.data);

    const scanRes = await axios.get("/api/putaway/asn-scan", {
      params: { asn: asnValue }
    });

    setShipperID(scanRes.data?.ShipperID || "");
    setShipperName(scanRes.data?.ShipperName || "");
    setReceivedTotal(scanRes.data?.ReceivedPieces || 0);

    setAlreadyBinned(0);
    setRemaining(scanRes.data?.ReceivedPieces || 0);

    setSKU("");

    setTimeout(() => skuRef.current?.focus(), 200);
  }, [manualASN, selectedASN, asn]);

  useEffect(() => {
    if (!manualASN && asn.length > 3) {
      const t = setTimeout(() => handleASNInput(asn), 200);
      return () => clearTimeout(t);
    }
  }, [asn, manualASN, handleASNInput]);

  // ---------------- SKU ----------------
  const handleSKUChange = async (skuValue) => {
    const asnValue = manualASN ? selectedASN : asn;
    if (!asnValue || !skuValue) return;

    setSKU(skuValue);

    const res = await getASNDetails(asnValue, skuValue);

    setShipperID(res.data?.ShipperID || "");
    setShipperName(res.data?.ShipperName || "");

    setReceivedTotal(res.data?.ReceivedPieces || 0);
    setAlreadyBinned(res.data?.TotalPiecesBinned || 0);

    setRemaining(
      (res.data?.ReceivedPieces || 0) -
      (res.data?.TotalPiecesBinned || 0)
    );

    setTimeout(() => locRef.current?.focus(), 200);
  };

  // ---------------- QTY ----------------
  const handleQtyChange = (e) => {
    const qty = Number(e.target.value);
    const remain = receivedTotal - alreadyBinned - qty;

    if (remain < 0) {
      notify("❌ Pieces exceed available!", "error");
      setEnteredPieces("");
      setRemaining(receivedTotal - alreadyBinned);
      return;
    }

    setEnteredPieces(qty);
    setRemaining(remain);
  };

  // ---------------- SUBMIT ----------------
  const handleSubmit = async () => {
    const asnValue = manualASN ? selectedASN : asn;

    if (!asnValue || !sku || !locationId || !enteredPieces) {
      return notify("All fields required!", "error");
    }

    try {
      const res = await submitPutAway(
        asnValue,
        sku,
        locationId,
        enteredPieces,
        shipperID,
        shipperName,
        userId
      );

      notify(res.data.message);

      setAlreadyBinned(res.data.alreadyBinned);
      setRemaining(res.data.remaining);

      setLocationId("");
      setEnteredPieces("");

      setTimeout(() => pcsRef.current?.focus(), 200);
    } catch (err) {
      notify(err.response?.data?.message || "Error!", "error");
    }
  };

  // ---------------- UI ----------------
  return (
    <div className="putaway-wrapper">
      <ToastContainer />

      <div className="card">
        <h2 className="page-title">Put Away Operation</h2>

        {/* ASN */}
        <div className="section">
          <div className="section-header">ASN</div>

          <label className="check-row">
            <input
              type="checkbox"
              checked={manualASN}
              onChange={() => setManualASN(!manualASN)}
            />
            Manual ASN Entry
          </label>

          {manualASN ? (
            <select
              className="input"
              value={selectedASN}
              onChange={(e) => {
                setSelectedASN(e.target.value);
                handleASNInput(e.target.value);
              }}
            >
              <option value="">Select ASN</option>
              {asnList.map((x, i) => (
                <option key={i}>{x.ASN}</option>
              ))}
            </select>
          ) : (
            <input
              ref={asnRef}
              className="input"
              placeholder="Scan ASN"
              value={asn}
              onChange={(e) => setAsn(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleASNInput(asn)}
            />
          )}
        </div>

        {/* SKU */}
        <div className="section">
          <div className="section-header">SKU</div>

          <select
            ref={skuRef}
            className="input"
            value={sku}
            onChange={(e) => handleSKUChange(e.target.value)}
          >
            <option value="">Select SKU</option>
            {skuList.map((x, i) => (
              <option key={i}>{x.SKU}</option>
            ))}
          </select>

          <div className="info-panel">
            <div><b>Shipper:</b> {shipperName} ({shipperID})</div>
            <div><b>Received:</b> {receivedTotal}</div>
            <div><b>Binned:</b> {alreadyBinned}</div>
            <div><b>Remaining:</b> {remaining}</div>
          </div>
        </div>

        {/* LOCATION */}
        <div className="section">
          <div className="section-header">Location</div>

          <label className="check-row">
            <input
              type="checkbox"
              checked={manualLoc}
              onChange={() => setManualLoc(!manualLoc)}
            />
            Manual Location Entry
          </label>

          {manualLoc ? (
            <select
              className="input"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">Select Location</option>
              {locationList.map((l, i) => (
                <option key={i}>{l.LocationID}</option>
              ))}
            </select>
          ) : (
            <input
              ref={locRef}
              className="input"
              placeholder="Scan Location"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            />
          )}
        </div>

        {/* QTY */}
        <div className="section">
          <div className="section-header">Quantity</div>

          <input
            ref={pcsRef}
            className="input"
            placeholder="Enter Quantity"
            value={enteredPieces}
            onChange={handleQtyChange}
          />
        </div>

        <button className="submit-btn" onClick={handleSubmit}>
          Submit Put Away
        </button>
      </div>

      {/* ✅ STYLE */}
      <style>{`
        .putaway-wrapper {
          background: #f3f4f6;
          min-height: 100vh;
          padding: 20px;
          display: flex;
          justify-content: center;
        }
        .card {
          width: 97%;
          max-width: 800px;
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .page-title {
          text-align: center;
          color: #C30017;
          margin-bottom: 20px;
        }
        .section {
          margin-bottom: 20px;
        }
        .section-header {
          font-weight: bold;
          margin-bottom: 6px;
        }
        .input {
          width: 97%;
          padding: 12px;
          border-radius: 6px;
          border: 1px solid #ccc;
        }
        .check-row {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .info-panel {
          margin-top: 10px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          padding: 10px;
          border-radius: 8px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 6px;
        }
        .submit-btn {
          background: #C30017;
          color: white;
          padding: 14px;
          border: none;
          border-radius: 10px;
          font-weight: bold;
          width: 50%;
        }
      `}</style>
    </div>
  );
}